import { Link, useNavigate } from 'react-router-dom';
import type { User } from '@chicare/shared';

import { useLogout } from '@/lib/session';
import { homeForRole } from '@/lib/roleHome';
import { Icon } from '@/components/icons/Icon';

/**
 * "You are signed in, but not here."
 *
 * ── WHY IT HAS BUTTONS NOW ──────────────────────────────────────────────────
 *
 * This screen used to be a full stop. It named the account, explained the
 * account was wrong for this area, and offered nothing at all — no way to the
 * console that account CAN use, and no way to sign out and try another. A
 * pharmacy owner who reached it had to know to edit the address bar, and most
 * people reasonably conclude the app is broken instead.
 *
 * So it now does the two things somebody standing here actually wants: go
 * where I can go, or let me in as somebody else.
 *
 * The API remains the security boundary — `auth:sanctum` and the role
 * middleware refuse the request whatever this renders. This is the explanation,
 * not the lock.
 */
export function WrongDoor({
  title,
  // Replaces the default explanation when the reason is not "wrong building".
  // A colleague whose role does not cover a screen is not at the wrong door,
  // and telling them so sends them to ask the wrong question.
  body,
  user,
}: {
  title: string;
  body?: string;
  user: User | null | undefined;
}) {
  const navigate = useNavigate();
  const logout = useLogout();

  const home = homeForRole(user?.role);
  const homeLabel = LABELS[home] ?? 'the shop';

  return (
    <div className="mx-auto grid min-h-[60vh] w-full max-w-[520px] place-items-center px-4">
      <div className="text-center">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-caution-tint">
          <Icon name="alert" size={26} className="text-caution" />
        </div>

        <h1 className="m-0 font-display text-[19px] font-bold tracking-[-0.02em]">{title}</h1>

        <p className="mt-2 text-[13px] leading-relaxed text-muted">
          You are signed in as <b className="text-ink">{user?.name}</b>
          {user ? <> — {describe(user.role)}</> : null}.{' '}
          {body ?? 'Nothing is wrong with your account; it simply opens a different part of ChiCare.'}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <Link
            to={home}
            className="inline-flex min-h-11 items-center rounded-btn bg-blue px-4 text-[13.5px] font-bold text-white no-underline"
          >
            Go to {homeLabel}
          </Link>

          <button
            type="button"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                // Back to the door they were trying, so signing in as the
                // right account finishes what they came to do.
                onSettled: () => navigate('/', { replace: true }),
              })
            }
            className="inline-flex min-h-11 items-center rounded-btn border border-line bg-white px-4 text-[13.5px] font-semibold text-ink"
          >
            {logout.isPending ? 'Signing out…' : 'Sign in as someone else'}
          </button>
        </div>
      </div>
    </div>
  );
}

const LABELS: Record<string, string> = {
  '/admin': 'the console',
  '/seller': 'your pharmacy',
  '/rider': 'your runs',
  '/': 'the shop',
};

/** Named properly. Everything that was not a pharmacy used to be called a buyer. */
function describe(role: User['role']): string {
  switch (role) {
    case 'pharmacy':
      return 'a pharmacy account';
    case 'rider':
      return 'a rider account';
    case 'pharmacist':
      return 'a ChiCare pharmacist account';
    case 'admin':
    case 'super_admin':
      return 'a ChiCare staff account';
    default:
      return 'a buyer account';
  }
}
